import { db } from '../config/firebase';
import {
    collection,
    writeBatch,
    doc,
    getDocs,
    query,
    orderBy,
    getDoc,
} from 'firebase/firestore';
import { logger, getEnrolmentLogs } from '../util/logger';

export async function saveAttendees(attendees) {
    const batch = writeBatch(db);
    const collectionRef = collection(db, 'SummerSchool-2026');
    const docRefs = [];

    // Prepare Firestore batch
    for (const attendee of attendees) {
        const newDocRef = doc(collectionRef);
        batch.set(newDocRef, attendee);
        docRefs.push(newDocRef);
    }

    // Commit to Firestore
    await batch.commit();

    // Log successful save
    const logPromises = attendees.map(async (attendee, index) => {
        const docId = docRefs[index].id;
        return logger.info(
            'Enrolment saved',
            attendee,
            docId // passing document ID as third parameter
        );
    });

    // Verify all documents were written
    const verificationPromises = docRefs.map(async (docRef, index) => {
        const docSnap = await getDoc(docRef);
        if (!docSnap.exists()) {
            throw new Error(
                `Document ${docRef.id} was not written successfully`
            );
        }
        const data = docSnap.data();
        // Verify essential data matches what we tried to write
        if (
            data.firstName !== attendees[index].firstName ||
            data.lastName !== attendees[index].lastName ||
            data.registrationDate !== attendees[index].registrationDate
        ) {
            throw new Error(`Document ${docRef.id} data mismatch`);
        }
    });

    // Wait for all verifications to complete
    await Promise.all([...logPromises, ...verificationPromises]);

    // If we get here, all documents were written and verified
    return docRefs.map((ref) => ref.id);
}

export async function getEnrolments() {
    const collectionRef = collection(db, 'SummerSchool-2026');
    const q = query(collectionRef, orderBy('registrationDate'));
    return getDocs(q);
}

/**
 * Audit enrolments against logs
 * @returns {Promise<Array>} Array of missing enrolments
 */
export async function auditEnrolments() {
    // Get all current enrolments
    const enrolmentsSnapshot = await getEnrolments();
    const currentEnrolments = new Set(
        enrolmentsSnapshot.docs.map((doc) => doc.id)
    );

    // Get all enrolment logs
    const logs = await getEnrolmentLogs();

    // Find missing enrolments (in logs but not in current enrolments)
    const missingEnrolments = [];
    for (const log of logs) {
        if (!currentEnrolments.has(log.documentId)) {
            missingEnrolments.push({
                documentId: log.documentId,
                ...log.metadata,
                timestamp: log.timestamp,
            });
        }
    }

    return missingEnrolments;
}
